import React, { useState, useEffect, useCallback, useRef } from 'react'
import { descriptionTemplatesApi } from '../../services/descriptionTemplates'
import type { DescriptionTemplate } from '../../services/descriptionTemplates'
import { Toast } from '../shared/Toast'
import { ConfirmDialog } from '../shared/ConfirmDialog'

// Template modal
function TemplateModal({ template, onSave, onClose }: {
  template: DescriptionTemplate | null
  onSave: (data: { name: string; content: string; is_default: boolean }) => Promise<void>
  onClose: () => void
}) {
  const [name, setName] = useState(template?.name || '')
  const [content, setContent] = useState(template?.content || '')
  const [isDefault, setIsDefault] = useState(template?.is_default || false)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const nameRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    nameRef.current?.focus()
  }, [])

  const validate = () => {
    const newErrors: Record<string, string> = {}
    if (!name.trim()) newErrors.name = 'Name is required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    setSaving(true)
    try {
      await onSave({ name: name.trim(), content: content.trim(), is_default: isDefault })
    } catch {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-secondary">
              {template ? 'Edit Template' : 'New Template'}
            </h3>
          </div>

          <div className="px-6 py-5 space-y-4">
            <div>
              <label htmlFor="template-name" className="block text-sm font-medium text-secondary mb-1">
                Template Name <span className="text-red-500">*</span>
              </label>
              <input
                ref={nameRef}
                id="template-name"
                type="text"
                value={name}
                onChange={e => { setName(e.target.value); setErrors({}) }}
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary ${
                  errors.name ? 'border-red-400' : 'border-gray-300'
                }`}
                placeholder="e.g., Standard Vehicle Description"
              />
              {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
            </div>

            <div>
              <label htmlFor="template-content" className="block text-sm font-medium text-secondary mb-1">
                Content
              </label>
              <textarea
                id="template-content"
                value={content}
                onChange={e => setContent(e.target.value)}
                rows={8}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-y"
                placeholder="Enter the template content. Use placeholders like {{year}}, {{make}}, {{model}}, {{mileage}}..."
              />
              <p className="mt-1 text-xs text-grey">
                Available placeholders: {'{{year}}'}, {'{{make}}'}, {'{{model}}'}, {'{{mileage}}'}, {'{{features}}'}
              </p>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={e => setIsDefault(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary/30"
              />
              <span className="text-sm text-secondary">Set as default template</span>
            </label>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-grey border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? 'Saving...' : template ? 'Update Template' : 'Create Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export function DescriptionTemplates(): React.ReactElement {
  const [templates, setTemplates] = useState<DescriptionTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [modalTemplate, setModalTemplate] = useState<DescriptionTemplate | null | 'new'>(null)
  const [deleteTarget, setDeleteTarget] = useState<DescriptionTemplate | null>(null)
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  const loadTemplates = useCallback(async () => {
    try {
      const data = await descriptionTemplatesApi.list()
      setTemplates(data)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load templates')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadTemplates() }, [loadTemplates])

  const showToast = (message: string, type: 'success' | 'error') => setToast({ message, type })

  const handleSave = async (data: { name: string; content: string; is_default: boolean }) => {
    try {
      if (modalTemplate && modalTemplate !== 'new') {
        const updated = await descriptionTemplatesApi.update(modalTemplate.id, data)
        setTemplates(prev => {
          // If this template became default, clear default from others
          if (updated.is_default) {
            return prev.map(t => t.id === updated.id ? updated : { ...t, is_default: false })
          }
          return prev.map(t => t.id === updated.id ? updated : t)
        })
        showToast('Template updated', 'success')
      } else {
        const created = await descriptionTemplatesApi.create(data)
        setTemplates(prev => {
          if (created.is_default) {
            return [...prev.map(t => ({ ...t, is_default: false })), created]
          }
          return [...prev, created]
        })
        showToast('Template created', 'success')
      }
      setModalTemplate(null)
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to save template', 'error')
      throw err
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await descriptionTemplatesApi.destroy(deleteTarget.id)
      setTemplates(prev => prev.filter(t => t.id !== deleteTarget.id))
      setDeleteTarget(null)
      showToast('Template deleted', 'success')
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to delete template', 'error')
    }
  }

  const handleDragStart = (index: number) => {
    setDragIndex(index)
  }

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    setDragOverIndex(index)
  }

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault()
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null)
      setDragOverIndex(null)
      return
    }

    const reordered = [...templates]
    const [moved] = reordered.splice(dragIndex, 1)
    reordered.splice(dropIndex, 0, moved)
    setTemplates(reordered)
    setDragIndex(null)
    setDragOverIndex(null)

    try {
      const updated = await descriptionTemplatesApi.reorder(reordered.map(t => t.id))
      setTemplates(updated)
    } catch {
      showToast('Failed to reorder templates', 'error')
      loadTemplates()
    }
  }

  const handleDragEnd = () => {
    setDragIndex(null)
    setDragOverIndex(null)
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-sm text-grey mt-3">Loading templates...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mx-auto mb-3">
          <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
        </div>
        <p className="text-sm text-red-600 mb-3">{error}</p>
        <button onClick={loadTemplates} className="text-sm text-primary font-medium hover:underline">
          Try again
        </button>
      </div>
    )
  }

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {modalTemplate !== null && (
        <TemplateModal
          template={modalTemplate === 'new' ? null : modalTemplate}
          onSave={handleSave}
          onClose={() => setModalTemplate(null)}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Delete Template"
          message={`Are you sure you want to delete "${deleteTarget.name}"? This action cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-secondary">Description Templates</h2>
            <p className="text-sm text-grey mt-0.5">
              Create reusable templates for vehicle descriptions. Drag to reorder.
            </p>
          </div>
          <button
            onClick={() => setModalTemplate('new')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            New Template
          </button>
        </div>

        {/* Template List */}
        {templates.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <div className="w-14 h-14 bg-orange-50 rounded-xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-secondary mb-1">No templates yet</h3>
            <p className="text-sm text-grey mb-5 max-w-sm mx-auto">
              Create your first description template to quickly populate vehicle listings with consistent content.
            </p>
            <button
              onClick={() => setModalTemplate('new')}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              Create Template
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {templates.map((template, index) => (
              <div
                key={template.id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={e => handleDragOver(e, index)}
                onDrop={e => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                className={`bg-white rounded-xl border transition-all group ${
                  dragIndex === index
                    ? 'opacity-50 border-primary/40'
                    : dragOverIndex === index
                    ? 'border-primary border-2 shadow-md'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                }`}
              >
                <div className="flex items-start gap-3 p-4">
                  {/* Drag handle */}
                  <div className="mt-1 cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-400 shrink-0">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M7 2a2 2 0 10.001 4.001A2 2 0 007 2zm0 6a2 2 0 10.001 4.001A2 2 0 007 8zm0 6a2 2 0 10.001 4.001A2 2 0 007 14zm6-8a2 2 0 10-.001-4.001A2 2 0 0013 6zm0 2a2 2 0 10.001 4.001A2 2 0 0013 8zm0 6a2 2 0 10.001 4.001A2 2 0 0013 14z" />
                    </svg>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-semibold text-secondary truncate">{template.name}</h3>
                      {template.is_default && (
                        <span className="shrink-0 px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full">
                          Default
                        </span>
                      )}
                    </div>
                    {template.content ? (
                      <p className="text-sm text-grey line-clamp-2">{template.content}</p>
                    ) : (
                      <p className="text-sm text-gray-400 italic">No content</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => setModalTemplate(template)}
                      className="p-1.5 text-grey hover:text-secondary hover:bg-gray-100 rounded-lg"
                      title="Edit template"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setDeleteTarget(template)}
                      className="p-1.5 text-grey hover:text-red-600 hover:bg-red-50 rounded-lg"
                      title="Delete template"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}
